// src/pages/DataSummary.js
import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import './DataSummary.css';

const DataSummary = () => {
  const [serverId, setServerId] = useState('2');
  const [area, setArea] = useState('PCB');
  const [shift, setShift] = useState('A');
  const [productionDate, setProductionDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Hardcoded employee ID - same for everyone
  const EMPLOYEE_ID = 108831;

  const serverList = [
    { id: 2, name: 'RCP 1001' },
    { id: 3, name: 'HDR 1101' },
    { id: 4, name: 'HDR 3000' },
    { id: 5, name: 'HDR 1100' },
    { id: 6, name: 'Guwahati - 1200/1210'},
  ];

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0];
    setProductionDate(today);
  }, []);

  const handleGenerate = async () => {
    if (!serverId || !area || !shift || !productionDate) {
      setMessage('Please fill all required fields');
      setMessageType('error');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('Sending request with:', {
        serverId: parseInt(serverId),
        area: area,
        shift: shift,
        productionDate: productionDate,
        employeeId: EMPLOYEE_ID
      });
      
      const result = await apiService.generateLineSummary({
        serverId: parseInt(serverId),
        area: area,
        shift: shift,
        productionDate: productionDate,
        employeeId: EMPLOYEE_ID
      });

      if (result.success) {
        setMessage(
          `✅ ${result.message} | Lines Processed: ${result.linesProcessed} | Shift: ${result.shift} | Date: ${result.date}`
        );
        setMessageType('success');
      } else {
        setMessage(`❌ ${result.message}`);
        setMessageType('error');
      }
    } catch (error) {
      console.error('Generation error:', error);
      setMessage(`❌ Error: ${error.message}`);
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="data-summary-page">
      <div className="page-header">
        <h1>📊 Data Summary Generation</h1>
        <p>Generate daily production line summaries for analytics and reporting</p>
      </div>

      <div className="summary-card">
        <div className="card-title">
          <h2>Generate Summary</h2>
        </div>

        <div className="form-layout">
          <div className="form-row">
            <div className="input-group">
              <label>Plant <span className="required-star">*</span></label>
              <select
                value={serverId}
                onChange={(e) => setServerId(e.target.value)}
                disabled={loading}
                className="input-field"
              >
                <option value="">Select Server</option>
                {serverList.map((server) => (
                  <option key={server.id} value={server.id}>
                    {server.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="input-group">
              <label>Area <span className="required-star">*</span></label>
              <select
                value={area}
                onChange={(e) => setArea(e.target.value)}
                disabled={loading}
                className="input-field"
              >
                <option value="PCB">PCB</option>
                <option value="EMA">EMA</option>
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="input-group">
              <label>Shift <span className="required-star">*</span></label>
              <select
                value={shift}
                onChange={(e) => setShift(e.target.value)}
                disabled={loading}
                className="input-field"
              >
                <option value="A">Shift A</option>
                <option value="B">Shift B</option>
              </select>
            </div>

            <div className="input-group">
              <label>Production Date <span className="required-star">*</span></label>
              <input
                type="date"
                value={productionDate}
                onChange={(e) => setProductionDate(e.target.value)}
                disabled={loading}
                className="input-field"
                max={new Date().toISOString().split('T')[0]}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="input-group full-width">
              <label>Employee ID (Default)</label>
              <input
                type="text"
                value={EMPLOYEE_ID}
                disabled
                className="input-field disabled-field"
                readOnly
              />
            </div>
          </div>

          {message && (
            <div className={`alert-message ${messageType}`}>
              {message}
            </div>
          )}

          <button
            className="submit-button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="loader"></span>
                Generating...
              </>
            ) : (
              <>
                ⚡ Generate Summary
              </>
            )}
          </button>
        </div>

        <div className="info-box">
          <div className="info-title">ℹ️ Information</div>
          <ul className="info-list">
            <li>Generates daily production summaries from tracking data</li>
            <li>Safe to re-run - existing records will be updated</li>
            <li>Processing time varies based on active lines</li>
            <li>Select appropriate shift (A/B/C) for accurate reporting</li>
            <li>Employee ID 108831 is used by default for all summary generations</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataSummary;